import {merge, partition, sortBy} from 'lodash';
import AssessmentResult from '../../../values/AssessmentResult';
import Assessment from '../assessment';
import {MAIN_CONTENT_POINTS, SENTENCE_BEGINNINGS_ID} from '@axyseo/const/analysis';

const maximumConsecutiveDuplicates = 2;

/**
 * Represents the assessment that checks whether there are three or more consecutive sentences beginning with the same word.
 */
export default class SentenceBeginningsAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {object} config The configuration to use.
   *
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: SENTENCE_BEGINNINGS_ID,
      priority: 'medium',
      ctaType: 'fix',
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-on-page-audit/checklist#sentence-beginnings',
      fixPosition: 'description',
      title: 'Sentence beginnings',
      content: {
        improve: '',
        bad:
          'Found sentences with repetitive beginnings. Change the wording for more engaging content.',
        good: 'No sentences with repetitive beginnings found.'
      }
    };

    this.identifier = SENTENCE_BEGINNINGS_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Counts and groups the number too often used sentence beginnings and determines the lowest count within that group.
   *
   * @param {array} sentenceBeginnings The array containing the objects containing the beginning words and counts.
   *
   * @returns {object} The object containing the total number of too often used beginnings and the lowest count within those.
   */
  groupSentenceBeginnings(sentenceBeginnings) {
    const tooOften = partition(sentenceBeginnings, function(word) {
      return word.count > maximumConsecutiveDuplicates;
    });

    if (tooOften[0].length === 0) {
      return {total: 0};
    }

    const sortedCounts = sortBy(tooOften[0], function(word) {
      return word.count;
    });

    return {total: tooOften[0].length, lowestCount: sortedCounts[0].count};
  }

  /**
   *
   * @param groupedSentenceBeginnings
   * @returns {{score: number, status: string}}
   */
  calculateSentenceBeginningsResult(groupedSentenceBeginnings) {
    let status = '';
    if (groupedSentenceBeginnings.total > 0) {
      status = 'bad';
    } else {
      status = 'good';
    }

    const score = this.getScore(MAIN_CONTENT_POINTS, status);

    return {
      score,
      status
    };
  }

  /**
   *
   * @param paper
   * @param researcher
   * @returns {AssessmentResult}
   */
  getResult({paper, researcher}) {
    const sentenceBeginnings = researcher.getResearch('getSentenceBeginnings');
    const sentenceBeginningsErrors = sentenceBeginnings.filter(
      sentenceBeginning => sentenceBeginning.count > maximumConsecutiveDuplicates
    );

    const groupedSentenceBeginnings = this.groupSentenceBeginnings(sentenceBeginnings);
    const calculatedResult = this.calculateSentenceBeginningsResult(groupedSentenceBeginnings);
    const assessmentResult = new AssessmentResult({config: this._config});

    assessmentResult.setData(sentenceBeginningsErrors);
    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);

    return assessmentResult;
  }

  /**
   * Checks if the sentence beginnings assessment is applicable to the paper.
   *
   * @param {Object}      paper       The paper to check.
   * @param {Researcher}  researcher  The researcher object.
   *
   * @returns {boolean} Returns true if the language is available and the paper is not empty.
   */
  isApplicable(paper, researcher) {
    return (
      this.hasEnoughContentForAssessment(paper) && researcher.hasResearch('getSentenceBeginnings')
    );
  }
}
